import {
  Dialog, DialogContent, DialogTitle, IconButton, Stack, Tooltip, useMediaQuery,
} from '@mui/material';
import type { Theme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';
import { FichaContenido } from '../pages/Ficha.js';

/** La ficha de una película o una serie en un diálogo sobre la pantalla que la abrió.
 *
 *  Desde la watchlist se abre una ficha, se descarga algo y se vuelve; navegando a
 *  `/pelicula/:id` esa vuelta era otra pantalla: se perdían la página del paginador, el
 *  filtro y el término de búsqueda, y con mil películas eso es volver a buscar la fila. El
 *  diálogo deja la lista tal cual debajo. La página completa sigue existiendo y se llega a
 *  ella desde la cabecera, para quien quiera la ficha en su propia dirección. */
export function DialogoFicha({
  id, kind, onCerrar,
}: {
  id: string;
  kind: 'movie' | 'show';
  onCerrar: () => void;
}) {
  const qc = useQueryClient();
  // En una pantalla estrecha la tabla de versiones no cabe en un diálogo flotante: a
  // pantalla completa se desplaza como la página.
  const pantallaCompleta = useMediaQuery((t: Theme) => t.breakpoints.down('md'));

  const cerrar = () => {
    // Lo que se haya pedido dentro tiene que verse en la fila al volver: la descarga nueva
    // y su estado salen de esta consulta, no de la ficha.
    void qc.invalidateQueries({ queryKey: ['watchlist'] });
    onCerrar();
  };

  return (
    <Dialog open onClose={cerrar} fullWidth maxWidth="lg" fullScreen={pantallaCompleta}>
      <DialogTitle sx={{ py: 1, pr: 1 }}>
        <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', justifyContent: 'flex-end' }}>
          <Tooltip title="Abrir la ficha en su propia página">
            <IconButton
              size="small"
              component={RouterLink}
              to={`/${kind === 'show' ? 'serie' : 'pelicula'}/${id}`}
              aria-label="Abrir la ficha en su propia página"
            >
              <OpenInNewIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <IconButton size="small" onClick={cerrar} aria-label="Cerrar">
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent sx={{ pt: 0 }}>
        <FichaContenido id={id} />
      </DialogContent>
    </Dialog>
  );
}
